import React, { useState } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../LanguageContext";
import { ChevronLeft, ChevronRight } from "lucide-react";

import imgBig2024 from "../assets/2024/dms1.jpg";
import imgSmall2024a from "../assets/2024/dms2.jpg";
import imgSmall2024b from "../assets/2024/dms3.jpg";
import imgSmall2024c from "../assets/2024/dms4.jpg";

import imgBig2025 from "../assets/2025/omc.jpg";
import imgSmall2025a from "../assets/2025/das2.jpeg";
import imgSmall2025b from "../assets/2025/das3.jpeg";
import imgSmall2025c from "../assets/2025/das4.jpeg";

import imgBig2023 from "../assets/2023/i3.jpg";
import imgSmall2023a from "../assets/2023/i1.png";
import imgSmall2023b from "../assets/2023/i2.jpg";
import imgSmall2023c from "../assets/2023/i4.jpg";

const zoomIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.8, ease: "easeOut" } },
};


const OurJourney = () => {
  const { language } = useLanguage();
  const isIndo = language === "id";

  const timelineData = [
    {
      year: 2023,
      title: isIndo ? "Pemasangan iDasWmeter" : "iDasWmeter Deployment",
      description: isIndo
        ? "iDasWmeter — Flowmeter digital portabel dengan katup otomatis untuk pengisian air tawar kapal yang dikendalikan lewat aplikasi seluler. Dipasang di Pelabuhan Tanjung Priok pada Maret 2023, solusi ini meningkatkan efisiensi pengisian, mengurangi risiko tumpahan, dan memungkinkan pemantauan real-time dari mana saja."
        : "iDasWmeter — a portable digital flowmeter with an automatic valve for ship freshwater bunkering, controlled via a mobile app. Installed at Tanjung Priok Port in March 2023, this solution streamlines bunkering operations, reduces spill risk, and enables real-time monitoring from anywhere.",
      images: [imgBig2023, imgSmall2023a, imgSmall2023b, imgSmall2023c],
    },
    {
      year: 2024,
      title: isIndo ? "Ekspansi Device Monitoring System (DMS)" : "Device Monitoring System (DMS) Expansion",
      description: isIndo
        ? "Tahun 2024 menjadi tonggak penting bagi pengembangan Device Monitoring System (DMS). Dengan kemampuan Multiple Protocol Streaming Data Relay Proteksi, DMS dipasang di berbagai gardu induk strategis seperti Godean, Indobharat, Purwakarta, Dieng, dan Gejayan. Sistem ini juga mendukung kesiagaan Rakernas Kabinet 2024 di Magelang, memperkuat efisiensi dan keandalan jaringan kelistrikan nasional."
        : "2024 marked a milestone year for the Device Monitoring System (DMS). Featuring Multiple Protocol Streaming Data Relay Protection, DMS was deployed across major substations including Godean, Ngimbang 500kV, Indobharat, Purwakarta, Dieng, and Gejayan. It also supported the 2024 National Cabinet Meeting in Magelang, enhancing efficiency and reliability across Indonesia’s power network.",
      images: [imgBig2024, imgSmall2024a, imgSmall2024b, imgSmall2024c],
    }
    ,
    {
      year: 2025,
      title: isIndo ? "Inovasi Ekosistem digital OMC (Operator Maintenance Centre)" : "Digital Alert System (DAS) Innovation",
      description: isIndo
        ? "Pada tahun 2025, Micronet Engineering Laboratories Memperkenalkan Ekosistem OMC yang mengintegrasikan perangkat - perangkat smart monitoring kami seperti DAS24, DAS48 dan BMS kedalam sebuah aplikasi HMI (Human Machine Interface) yang informatif. Perangkat kami telah dipasang di berbagai lokasi strategis seperti  Gardu Induk Indobharat dan Cibatu, memperkuat sistem monitoring serta proteksi jaringan listrik nasional."
        : "In 2025, Micronet Engineering Laboratories introduced the OMC Ecosystem, which integrates our smart monitoring devices, such as the DAS24, DAS48, and BMS, into an informative HMI (Human Machine Interface) application. Our devices have been installed in various strategic locations, such as the Indobharat and Cibatu substations, strengthening the monitoring and protection system of the national electricity grid.",
      images: [imgBig2025, imgSmall2025a, imgSmall2025b, imgSmall2025c],
    },
  ];

  const [currentImage, setCurrentImage] = useState(0);
  const [startIndex, setStartIndex] = useState(0);


  const [selectedYear, setSelectedYear] = useState(timelineData[0].year);
  const activeData = timelineData.find((item) => item.year === selectedYear);

  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? activeData.images.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === activeData.images.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <section className="w-full bg-white py-16" id="journey">
      <div className="max-w-[1600px] mx-auto lg:px-10 flex flex-col gap-12">
        <div className="flex flex-col md:flex-row md:items-start md:gap-12">
          <div className="md:w-1/2">
            <motion.div variants={zoomIn} initial="hidden" whileInView="visible" viewport={{ once: true }}>
              <h2 className="text-4xl sm:text-5xl font-bold mb-4 text-[#1446A5]">
                {isIndo ? "Perjalanan Kami" : "Our Journey"}
              </h2>
              <p className="text-gray-600 leading-relaxed">
                {isIndo
                  ? "Micronet Engineering Laboratories (MEL) adalah perusahaan solusi IT yang berfokus pada pengembangan dan implementasi teknologi cerdas berbasis Internet of Things (IoT). Kami berkomitmen membantu bisnis bertransformasi secara digital dengan solusi inovatif yang mempercepat proses, meningkatkan efisiensi, dan menghadirkan informasi akurat untuk pengambilan keputusan yang lebih efektif."
                  : "Micronet Engineering Laboratories (MEL) is an IT solutions company focused on developing and implementing intelligent technologies based on the Internet of Things (IoT). We are committed to helping businesses achieve digital transformation through innovative solutions that accelerate processes, enhance efficiency, and provide accurate information for more effective decision-making."}
              </p>
            </motion.div>

            <motion.div
              variants={zoomIn}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-gray-50 rounded-2xl p-6 md:p-8 shadow-sm mt-10 md:mt- md:-ml-2 lg:-ml-2"
            >
              <h3 className="text-2xl md:text-2xl font-bold mb-3 text-[#1446A5]">
                {activeData.title}
              </h3>
              <p className="text-gray-700 md:text ">
                {activeData.description}
              </p>
            </motion.div>
          </div>

          <motion.div
            variants={zoomIn}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="md:w-1/2 flex flex-col justify-start mt-10 md:mt-0"
          >
            <img
              src={activeData.images[currentIndex]}
              alt={activeData.title}
              className="rounded-2xl w-full h-80 md:h-96 object-cover shadow-md mb-4"
            />
            <div className="flex items-center gap-3">
              <button
                onClick={handlePrev}
                className="bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-md"
              >
                <ChevronLeft size={22} />
              </button>
              <div className="grid grid-cols-3 gap-3 flex-1">
                {activeData.images.slice(1, 4).map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt=""
                    onClick={() => setCurrentIndex(idx + 1)}
                    className={`rounded-xl h-20 md:h-24 w-full object-cover cursor-pointer transition-transform duration-200 ${currentIndex === idx + 1
                      ? "ring-2 ring-red-600 scale-105"
                      : "hover:scale-105"
                      }`}
                  />
                ))}
              </div>

              <button
                onClick={handleNext}
                className="bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-md"
              >
                <ChevronRight size={22} />
              </button>
            </div>
          </motion.div>
        </div>

        <motion.div
          variants={zoomIn}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className=" pt-8 border-t border-gray-200"
        >
          <div className="relative flex justify-between items-center">
            <div className="absolute top-2 left-0 w-full h-0.5 bg-gray-200 -z-10"></div>
            {timelineData.map((item) => (
              <button
                key={item.year}
                onClick={() => {
                  setSelectedYear(item.year);
                  setCurrentIndex(0);
                }}
                className="flex flex-col items-center focus:outline-none group"
              >
                <div
                  className={`w-4 h-4 rounded-full mb-2 transition-all duration-300 ${selectedYear === item.year
                    ? "bg-red-600 scale-125"
                    : "bg-gray-400"
                    }`}
                ></div>
                <span
                  className={`text-sm ${selectedYear === item.year
                    ? "text-red-600 font-semibold"
                    : "text-gray-500"
                    }`}
                >
                  {item.year}
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default OurJourney;
